// src/Components/Cart/CartList.tsx

import React, { useState } from 'react';
import { formatPrice } from '../../Formatter/formatPrice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../Redux/Store';
import { checkoutInOrder, fetchOrderMenus } from '../../Redux/Public/orderApi';
import {
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Drawer,
  Divider,
  CardMedia,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ImageNotSupported as ImageNotSupportedIcon,
} from '@mui/icons-material';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';

interface CartItemOption {
  id: string;
  menuOptionId: string | null;
  name: string;
  price: number;
  quantity: number;
}

interface CartItem {
  id: string;
  menuId: string;
  menuOptionId: string | null;
  name: string;
  imageUrl: string;
  price: number;
  quantity: number;
  options?: CartItemOption[];
}

interface CartListProps {
  cart: CartItem[];
  onClose: () => void;
  onQuantityChange: (menuId: string, menuOptionId: string | null, newQuantity: number) => void;
  onRemoveItem: (cartId: string, menuId: string, menuOptionId: string | null) => void;
  handleConfirmDelete: () => void;
  activeStoreId: string;
  activeDiningTableId: string;
  onCheckoutSuccess: () => void;
  clerkId?: string;
  /** 店員が会計リクエストを受領済みで新規注文を受け付けないとき true（ADR 0068）。注文ボタンを無効化する。 */
  orderingClosed?: boolean;
}

const CartList: React.FC<CartListProps> = ({
  cart,
  onClose,
  onQuantityChange,
  onRemoveItem,
  handleConfirmDelete,
  activeStoreId,
  activeDiningTableId,
  onCheckoutSuccess,
  clerkId,
  orderingClosed = false,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [orderMessage, setOrderMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ cartId: string; menuId: string; menuOptionId: string | null } | null>(null);
  const CLOUDFRONT_URL = process.env.REACT_APP_CLOUDFRONT_URL;

  const totalAmount = cart.reduce((sum, item) => {
    if (!item) return sum;
    if (item.options) {
      return sum + item.options.reduce((optionSum, option) => optionSum + option.price * option.quantity, 0);
    } else {
      return sum + item.price * item.quantity;
    }
  }, 0);

  const handleCheckout = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmCheckout = async () => {
    setShowConfirmModal(false);
    setIsProcessing(true);
    const userType = clerkId ? 'clerk' : 'customer';
    try {
      await dispatch(checkoutInOrder({ activeStoreId, activeDiningTableId, userType })).unwrap();
      setOrderMessage('注文を受け付けました。');
    } catch (error: any) {
      // BEのエラーメッセージ（例: 「◯◯」は売り切れのためご注文いただけません）をそのまま表示する
      setOrderMessage(typeof error === 'string' && error ? error : '注文の確定に失敗しました。もう一度お試しください。');
      // 売り切れ等で拒否された場合に備え、メニューを再取得して売り切れ表示に同期する
      dispatch(fetchOrderMenus({
        active_store_id: activeStoreId,
        active_dining_table_id: activeDiningTableId,
      }));
    } finally {
      setIsProcessing(false);
      setShowOrderConfirmation(true);
    }
  };

  const handleCloseOrderConfirmation = () => {
    setShowOrderConfirmation(false);
    onClose();

    if (orderMessage === '注文を受け付けました。') {
      onCheckoutSuccess();
    }
  };

  const handleDeleteClick = (cartId: string, menuId: string, menuOptionId: string | null) => {
    onRemoveItem(cartId, menuId, menuOptionId);  
    setItemToDelete({ cartId, menuId, menuOptionId });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteClick = () => {
    if (itemToDelete) {
      handleConfirmDelete();  
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 } },
      }}
    >
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">カート内容</Typography>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <List sx={{ flex: 1, overflow: 'auto' }}>
          {cart.map((item) => (
            <ListItem key={item.id} divider>
              <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                {item.imageUrl ? (
                  <CardMedia
                    component="img"
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: 'contain',
                      bgcolor: 'grey.100',
                    }}
                    image={`${CLOUDFRONT_URL}${item.imageUrl}`}
                    alt={item.name}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'action.disabledBackground',
                      borderRadius: 1,
                    }}
                  >
                    <ImageNotSupportedIcon
                      sx={{
                        fontSize: 32,
                        color: 'action.disabled',
                      }}
                    />
                  </Box>
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1">{item.name}</Typography>
                  {item.options?.map((option, index) => (
                    <Box key={option.id} sx={{ ml: 2, mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {option.name}
                      </Typography>
                      <Typography variant="body2" color="primary">
                        {formatPrice(option.price)}円
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <IconButton
                          size="small"
                          onClick={() => option.quantity > 1 && onQuantityChange(item.menuId, option.menuOptionId, option.quantity - 1)}
                          disabled={option.quantity <= 1}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography variant="body2">{option.quantity}</Typography>
                        <IconButton
                          size="small"
                          onClick={() => onQuantityChange(item.menuId, option.menuOptionId, option.quantity + 1)}
                        >
                          <AddIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(option.id, item.menuId, option.menuOptionId)}
                          sx={{ ml: 'auto' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                  {!item.options && (
                    <>
                      <Typography variant="h6" color="primary">
                        {formatPrice(item.price)}円
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <IconButton
                          size="small"
                          onClick={() => item.quantity > 1 && onQuantityChange(item.menuId, null, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography>{item.quantity}</Typography>
                        <IconButton
                          size="small"
                          onClick={() => onQuantityChange(item.menuId, null, item.quantity + 1)}
                        >
                          <AddIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(item.id, item.menuId, null)}
                          sx={{ ml: 'auto' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>

        <ConfirmDialog
          open={showDeleteConfirm}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDeleteClick}
          title="削除の確認"
          message="商品をカートから削除しますか？"
          confirmText="削除"
          confirmColor="error"
        />

        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper' }}>
          <Typography variant="h6" gutterBottom>
            合計: {formatPrice(totalAmount)}円（税抜）
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleCheckout}
            disabled={cart.length === 0 || orderingClosed}
          >
            注文する
          </Button>
          {orderingClosed && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'center' }}>
              お会計受付済みのためご注文いただけません
            </Typography>
          )}
        </Box>
      </Box>

      <Dialog open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
        <DialogTitle>注文の確認</DialogTitle>
        <DialogContent>
          <Typography>注文を確定しますか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmModal(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmCheckout}
            variant="contained"
            color="primary"
            disabled={isProcessing}
          >
            {isProcessing ? <CircularProgress size={24} /> : '確定する'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showOrderConfirmation} onClose={handleCloseOrderConfirmation}>
        <DialogTitle>
          {orderMessage === '注文を受け付けました。' ? '注文完了' : 'エラー'}
        </DialogTitle>
        <DialogContent>
          <Alert severity={orderMessage === '注文を受け付けました。' ? 'success' : 'error'}>
            {orderMessage}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOrderConfirmation} variant="contained">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default CartList;